import type { GridPosition, MapDefinition } from './types'

export interface PathDerivationError {
  rule: string
  message: string
}

export type PathDerivationResult =
  | { ok: true; path: GridPosition[] }
  | { ok: false; errors: PathDerivationError[] }

function cellKey(x: number, y: number): string {
  return `${x},${y}`
}

function sameCell(a: GridPosition, b: GridPosition): boolean {
  return a.x === b.x && a.y === b.y
}

function neighbors4(x: number, y: number, width: number, height: number): GridPosition[] {
  const out: GridPosition[] = []
  if (x > 0) out.push({ x: x - 1, y })
  if (x + 1 < width) out.push({ x: x + 1, y })
  if (y > 0) out.push({ x, y: y - 1 })
  if (y + 1 < height) out.push({ x, y: y + 1 })
  return out
}

/**
 * Derive a single ordered centerline path from PATH tiles.
 * Constraints (M10): one cell wide, orthogonal only, no branches, no loops.
 */
export function derivePathFromTiles(map: Pick<
  MapDefinition,
  'width' | 'height' | 'rows' | 'spawn' | 'exit'
>): PathDerivationResult {
  const errors: PathDerivationError[] = []
  const { width, height, rows, spawn, exit } = map

  if (!Number.isInteger(width) || width < 1 || !Number.isInteger(height) || height < 1) {
    return {
      ok: false,
      errors: [{ rule: 'dimensions', message: 'width and height must be positive integers' }]
    }
  }

  const inBounds = (p: GridPosition): boolean =>
    p.x >= 0 && p.y >= 0 && p.x < width && p.y < height

  if (!inBounds(spawn)) {
    errors.push({ rule: 'spawn-bounds', message: 'spawn is outside the map' })
  }
  if (!inBounds(exit)) {
    errors.push({ rule: 'exit-bounds', message: 'exit is outside the map' })
  }
  if (errors.length > 0) return { ok: false, errors }

  const isPath = (x: number, y: number): boolean => rows[y]?.[x] === 'P'

  if (!isPath(spawn.x, spawn.y)) {
    errors.push({ rule: 'spawn-tile', message: 'spawn must be on a PATH tile' })
  }
  if (!isPath(exit.x, exit.y)) {
    errors.push({ rule: 'exit-tile', message: 'exit must be on a PATH tile' })
  }
  if (errors.length > 0) return { ok: false, errors }

  const pathCells: GridPosition[] = []
  for (let y = 0; y < height; y += 1) {
    const row = rows[y] ?? ''
    for (let x = 0; x < width; x += 1) {
      if (row[x] === 'P') pathCells.push({ x, y })
    }
  }

  if (pathCells.length === 0) {
    return {
      ok: false,
      errors: [{ rule: 'path-empty', message: 'no PATH tiles to form a route' }]
    }
  }

  const pathSet = new Set(pathCells.map((c) => cellKey(c.x, c.y)))

  const degree = (p: GridPosition): number =>
    neighbors4(p.x, p.y, width, height).filter((n) => pathSet.has(cellKey(n.x, n.y))).length

  for (const cell of pathCells) {
    const d = degree(cell)
    if (d > 2) {
      errors.push({
        rule: 'path-branch',
        message: `Branch at (${cell.x},${cell.y}) — path must be one cell wide with no junctions`
      })
    }
  }
  if (errors.length > 0) return { ok: false, errors }

  // Walk uniquely from spawn. At each step pick the unused path neighbor.
  const ordered: GridPosition[] = [{ ...spawn }]
  const visited = new Set<string>([cellKey(spawn.x, spawn.y)])

  while (true) {
    const current = ordered[ordered.length - 1]
    const nextOptions = neighbors4(current.x, current.y, width, height).filter(
      (n) => pathSet.has(cellKey(n.x, n.y)) && !visited.has(cellKey(n.x, n.y))
    )

    if (nextOptions.length === 0) break
    if (nextOptions.length > 1) {
      return {
        ok: false,
        errors: [
          {
            rule: 'path-branch',
            message: `Branch at (${current.x},${current.y}) — multiple unused path neighbors`
          }
        ]
      }
    }

    const next = nextOptions[0]
    visited.add(cellKey(next.x, next.y))
    ordered.push(next)

    // Detect if we somehow revisit (should be impossible with visited set)
    if (ordered.length > pathCells.length + 1) {
      return {
        ok: false,
        errors: [{ rule: 'path-loop', message: 'Loop detected while walking the path' }]
      }
    }
  }

  if (!sameCell(ordered[ordered.length - 1], exit)) {
    return {
      ok: false,
      errors: [
        {
          rule: 'path-exit',
          message: 'spawn is not connected to exit along PATH tiles'
        }
      ]
    }
  }

  if (visited.size !== pathCells.length) {
    // Unvisited path cells: either disconnected component or a loop off the main chain
    const leftover = pathCells.filter((c) => !visited.has(cellKey(c.x, c.y)))
    const sample = leftover[0]
    // If leftover forms a closed loop attached... already blocked by degree>2.
    // Disconnected component or unreachable loop:
    const leftoverHasOnlyDegree2 = leftover.every((c) => degree(c) === 2)
    if (leftoverHasOnlyDegree2 && leftover.length >= 3) {
      return {
        ok: false,
        errors: [
          {
            rule: 'path-loop',
            message: `Loop detected near (${sample.x},${sample.y}) — unused PATH tiles form a cycle`
          }
        ]
      }
    }
    return {
      ok: false,
      errors: [
        {
          rule: 'path-disconnected',
          message: `Disconnected PATH at (${sample.x},${sample.y}) — all PATH tiles must form one spawn→exit chain`
        }
      ]
    }
  }

  return { ok: true, path: ordered }
}

/** Build a playable MapDefinition by deriving path from tiles. */
export function buildMapFromTiles(
  draft: Omit<MapDefinition, 'path'> & { path?: GridPosition[] }
): { map: MapDefinition; derivation: PathDerivationResult } {
  const derivation = derivePathFromTiles(draft)
  const path = derivation.ok ? derivation.path : draft.path ?? []
  return {
    map: {
      ...draft,
      path: path.map((cell) => ({ ...cell }))
    },
    derivation
  }
}

export function pathsEqual(a: GridPosition[], b: GridPosition[]): boolean {
  if (a.length !== b.length) return false
  return a.every((cell, index) => sameCell(cell, b[index]))
}
